import {HttpErrorResponse} from '@angular/common/http';
import {Component, Inject, LOCALE_ID, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {DomSanitizer, SafeStyle} from '@angular/platform-browser';
import {ActivatedRoute, Router} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {throwError} from 'rxjs';
import {catchError, first} from 'rxjs/operators';
import {PasswordResetComponent} from '~features/login/components/password-reset/password-reset.component';
import {FormLabelTranslationService} from '~shared/components/form-fields/translations/form-label-translations.service';
import {NotificationType} from '~core/enum';
import {AuthenticatedUser, Club, Country, JWTToken} from '~core/models';
import {AuthenticationService, CountryService, DefaultNotificationService, StorageService} from '~core/services';
import {ClubStatusTranslationsService, LoginTranslationsService, SharedTranslationsService,} from '~shared/translations';

/**
 * Login component, handles the log in of the user.
 */
@UntilDestroy()
@Component({ templateUrl: 'login.component.html' })
export class LoginComponent implements OnInit, OnDestroy {
  public loginForm: UntypedFormGroup;
  public loading: boolean = false;
  public submitted: boolean = false;
  public passwordResetModal: boolean = false;
  public returnUrl: string;
  public isValid: boolean = false;
  public countries: Array<Country> = [];
  public authenticatedUser: AuthenticatedUser;
  public image: SafeStyle;
  @ViewChild(PasswordResetComponent) public passwordReset: PasswordResetComponent;
  private jwtToken: JWTToken;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private authenticationService: AuthenticationService,
    private notificationService: DefaultNotificationService,
    private loginTranslations: LoginTranslationsService,
    @Inject(LOCALE_ID) public locale: any,
    private storage: StorageService,
    private countryService: CountryService,
    private sharedTranslations: SharedTranslationsService,
    private clubStatuses: ClubStatusTranslationsService,
    public flTranslations: FormLabelTranslationService,
    public sanitization: DomSanitizer
  ) {
    this.storage.castCurrentCountries
      .pipe(untilDestroyed(this))
      .subscribe((countries: Array<Country>) => this.countries = countries);
    if (this.storage.checkIfNotEmpty('token')) {
      this.storage.castAuthenticatedUser
        .pipe(untilDestroyed(this))
        .subscribe((user: AuthenticatedUser) => this.authenticatedUser = user);
    }
  }

  public ngOnDestroy(): void { }

  /**
   * After initialization create the login form. If user is logged in
   * redirect to /crm.
   */
  public ngOnInit(): void {
    this.loginForm = this.formBuilder.group({
      username: ['', Validators.required],
      password: ['', Validators.required]
    });

    if (this.authenticationService.isLoggedIn()) {
      if (this.authenticatedUser.isAdmin || this.authenticatedUser.isCallCenterAgent || this.authenticatedUser.isCountryManager) {
        this.returnUrl = '/manage/clubs';
        this.storage.clearSelectedClub();
      } else {
        this.returnUrl = `/club/'${this.authenticatedUser.clubs[0].id}`;
        this.storage.updateSelectedClub(this.authenticatedUser.clubs[0]);
      }
      this.router.navigate([this.returnUrl]);
    }
    const num = Math.floor(Math.random() * 5) + 1;
    const imageLink: string = `./assets/images/crm-login${num}.jpg`;
    this.image = this.sanitization.bypassSecurityTrustStyle(`url(${imageLink})`);
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';

    const externalLogin = localStorage.getItem('externalLogin');
    if (externalLogin !== null) {
      try {
        this.jwtToken = this.authenticationService.createJWTFromExternalJWTToken(externalLogin);
        this.loading = true;
        this.fetchUserAndCountries();
      } catch (error) {
        this.notificationService.showNotification({
          type: NotificationType.ALERT,
          message: this.loginTranslations.i18nErrorLoginBadToken
        });
      }
      localStorage.removeItem('externalLogin');
    }
  }

  /**
   * Convenience getter for easy access to form fields.
   */
  get f(): { [key: string]: AbstractControl } {
    return this.loginForm.controls;
  }

  /**
   * Handle the form. Log the user in if the credentials are correct.
   */
  public onSubmit(): any {
    this.submitted = true;

    // stop here if form is invalid
    if (this.loginForm.invalid) {
      this.isValid = false;
      return;
    }

    this.isValid = true;
    this.loading = true;
    this.authenticationService
      .login(this.f.username.value, this.f.password.value)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (token: JWTToken) => {
          this.jwtToken = token;
          this.fetchUserAndCountries();
        },
        error: (error: HttpErrorResponse) => {
          this.loading = false;
          this.notificationService.showNotification({
            type: NotificationType.ALERT,
            message: this.loginTranslations.i18nErrorLoginBadCredentials
          });
        }
      });
  }
  /**
   * Submits the form for the authenticated user
   */
  public fetchUserAndCountries(): any {
    this.authenticationService.fetchAuthenticatedUser()
      .pipe(
        untilDestroyed(this),
        catchError(err => {
          const forbidden = 403;
          if (err.status === forbidden) {
            this.loading = false;
            this.passwordResetModal = true;
            this.notificationService.showNotification({
              type: NotificationType.WARNING,
              message: this.loginTranslations.i18nErrorForbidden
            });
          }
          return throwError(err);
        })
      )
      .subscribe((user: any) => {
        const authenticatedUser = new AuthenticatedUser(user.user, user.permissions);
        authenticatedUser.setJWTToken = this.jwtToken;

        this.storage.setAuthenticatedUser(authenticatedUser);

        if (authenticatedUser.isAdmin || authenticatedUser.isCallCenterAgent || authenticatedUser.isCountryManager) {
          if (authenticatedUser.clubs.length === 1) {
            this.returnUrl = `/club/${authenticatedUser.clubs[0].id}`;
            this.storage.updateSelectedClub(new Club(authenticatedUser.clubs[0], this.clubStatuses));
          } else {
            this.returnUrl = '/manage/clubs';
            this.storage.clearSelectedClub();
          }
        } else {
          this.returnUrl = `/club/${authenticatedUser.clubs[0].id}`;
          this.storage.updateSelectedClub(new Club(authenticatedUser.clubs[0], this.clubStatuses));
        }
        if (this.countries === null) {
          this.countryService.getCountries()
            .pipe(first())
            .subscribe({
              next: fetchedCountries => {
                this.storage.setCurrentCountries(fetchedCountries, `${this.locale}-countries`);
              },
              error: error => {
                this.notificationService.showNotification({
                  type: NotificationType.ALERT,
                  message: this.sharedTranslations.i18nErrorFetchingCountries
                });
              }
            });
        } else {
          this.storage.loadCurrentCountries(`${this.locale}-countries`);
        }

        if (this.storage.redirectUrl) {
          this.router.navigateByUrl(this.storage.redirectUrl);
          this.storage.redirectUrl = null;
        } else {
          this.router.navigate([this.returnUrl]);
        }

      });
  }

  /**
   * Run this function after password has been reset
   */
  public afterCompleted(event: string): void {
    if (event === 'complete') {
      this.fetchUserAndCountries();
      this.notificationService.showNotification({
        type: NotificationType.SUCCESS,
        message: this.loginTranslations.i18nSuccessPasswordChange
      });
    }
  }
}
